import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PracticeResults = ({ 
  results, 
  onNewSession, 
  onContinue, 
  spacedRepetitionMode = false,
  reviewResults = []
}) => {
  const {
    accuracy = 0,
    timeElapsed = 0,
    pointsEarned = 0,
    versesCompleted = 0,
    perfectVerses = 0,
    hintsUsed = 0,
    newAchievements = []
  } = results;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const getPerformanceMessage = () => {
    if (accuracy >= 95) return { message: "Outstanding! Perfect memorization!", icon: "Trophy", color: "var(--color-success)" };
    if (accuracy >= 85) return { message: "Excellent work! Keep it up!", icon: "Star", color: "var(--color-success)" };
    if (accuracy >= 70) return { message: "Good progress! Practice makes perfect!", icon: "ThumbsUp", color: "var(--color-warning)" };
    return { message: "Keep practicing! You're improving!", icon: "Heart", color: "var(--color-primary)" };
  };

  const performance = getPerformanceMessage();

  const stats = [
    {
      label: 'Accuracy',
      value: `${accuracy}%`,
      icon: 'Target',
      color: accuracy >= 80 ? 'var(--color-success)' : 'var(--color-warning)'
    },
    {
      label: 'Time',
      value: formatTime(timeElapsed),
      icon: 'Clock',
      color: 'var(--color-primary)'
    },
    {
      label: 'Points',
      value: pointsEarned?.toLocaleString(),
      icon: 'Star',
      color: 'var(--color-accent)'
    },
    {
      label: 'Perfect',
      value: `${perfectVerses}/${versesCompleted}`,
      icon: 'Award',
      color: 'var(--color-success)'
    }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mx-auto mb-4">
          <Icon name={performance?.icon} size={32} color={performance?.color} />
        </div>
        <h2 className="text-2xl font-heading font-semibold text-card-foreground mb-2">
          Practice Complete!
        </h2>
        <p className="text-lg text-muted-foreground" style={{ color: performance?.color }}>
          {performance?.message}
        </p>
      </div>
      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {stats?.map((stat) => (
          <div key={stat?.label} className="bg-muted/30 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center mb-2">
              <Icon name={stat?.icon} size={20} color={stat?.color} />
            </div>
            <p className="text-xl font-mono font-semibold" style={{ color: stat?.color }}>
              {stat?.value}
            </p>
            <p className="text-sm font-caption text-muted-foreground">
              {stat?.label}
            </p>
          </div>
        ))}
      </div>
      {/* Detailed Stats */}
      <div className="bg-muted/20 rounded-lg p-4 mb-6">
        <h3 className="font-medium text-foreground mb-3">Session Details</h3>
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Verses Completed:</span>
            <span className="font-mono text-foreground">{versesCompleted}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Perfect Scores:</span>
            <span className="font-mono text-foreground">{perfectVerses}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Hints Used:</span>
            <span className="font-mono text-foreground">{hintsUsed}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Average Time per Verse:</span>
            <span className="font-mono text-foreground">
              {versesCompleted > 0 ? formatTime(Math.round(timeElapsed / versesCompleted)) : '0:00'}
            </span>
          </div>
        </div>
      </div>
      {/* New Achievements */}
      {newAchievements?.length > 0 && (
        <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-accent mb-3 flex items-center">
            <Icon name="Trophy" size={18} className="mr-2" />
            New Achievements!
          </h3>
          <div className="space-y-2">
            {newAchievements?.map((achievement, index) => (
              <div key={index} className="flex items-center space-x-2">
                <Icon name="Medal" size={16} color="var(--color-accent)" />
                <span className="text-sm text-accent-foreground">{achievement}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Enhanced Results Display for Spaced Repetition */}
      {spacedRepetitionMode && reviewResults?.length > 0 && (
        <div className="mt-6 p-4 bg-primary/10 border border-primary/20 rounded-lg">
          <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="Calendar" size={16} />
            Spaced Repetition Updates
          </h4>
          
          <div className="space-y-3">
            {/* Phase Advancement Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-success">
                  {reviewResults?.filter(r => r?.phase_advanced)?.length}
                </div>
                <div className="text-sm text-muted-foreground">Phases Advanced</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-warning">
                  {reviewResults?.filter(r => r?.phase_regressed)?.length}
                </div>
                <div className="text-sm text-muted-foreground">Phases Regressed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">
                  {reviewResults?.filter(r => !r?.phase_advanced && !r?.phase_regressed)?.length}
                </div>
                <div className="text-sm text-muted-foreground">Phases Maintained</div>
              </div>
            </div>

            {/* Detailed Phase Changes */}
            <div className="mt-4">
              <h5 className="text-sm font-medium text-foreground mb-2">Phase Changes:</h5>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {reviewResults?.map((result, index) => (
                  <div key={index} className="flex items-center justify-between text-sm bg-card border border-border p-2 rounded">
                    <span className="font-medium">Verse {index + 1}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Phase {result?.previous_phase || 1}</span>
                      <Icon 
                        name={result?.phase_advanced ? "ArrowUp" : result?.phase_regressed ? "ArrowDown" : "ArrowRight"} 
                        size={12} 
                        className={result?.phase_advanced ? "text-success" : result?.phase_regressed ? "text-destructive" : "text-muted-foreground"}
                      />
                      <span className="font-medium">Phase {result?.phase}</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        Next: {Math.round(result?.interval)} day{result?.interval !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Next Review Schedule */}
            <div className="mt-4 p-3 bg-card border border-border rounded">
              <h5 className="text-sm font-medium text-foreground mb-2">Next Review Schedule:</h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-muted-foreground">Tomorrow: </span>
                  <span className="font-medium">
                    {reviewResults?.filter(r => r?.interval <= 1)?.length} verses
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">This Week: </span>
                  <span className="font-medium">
                    {reviewResults?.filter(r => r?.interval > 1 && r?.interval <= 7)?.length} verses
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">This Month: </span>
                  <span className="font-medium">
                    {reviewResults?.filter(r => r?.interval > 7 && r?.interval <= 30)?.length} verses
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Quarterly: </span>
                  <span className="font-medium">
                    {reviewResults?.filter(r => r?.interval > 30)?.length} verses
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 mt-6">
        <Button
          variant="outline"
          onClick={onNewSession}
          className="flex-1"
          iconName="RotateCcw"
          iconPosition="left"
        >
          {spacedRepetitionMode ? 'Back to Practice Options' : 'New Session'}
        </Button>
        
        {!spacedRepetitionMode && (
          <Button
            onClick={onContinue}
            className="flex-1"
            iconName="Play"
            iconPosition="left"
          >
            Continue Practice
          </Button>
        )}

        {spacedRepetitionMode && (
          <Button
            onClick={() => window.location.href = '/verse-library'}
            className="flex-1"
            iconName="BookOpen"
            iconPosition="left"
          >
            View Library
          </Button>
        )}
      </div>
      {/* Encouragement Message */}
      <div className="mt-6 text-center">
        <p className="text-sm text-muted-foreground italic">
          "Your word I have hidden in my heart, that I might not sin against You." - Psalm 119:11
        </p>
      </div>
    </div>
  );
};

export default PracticeResults;